import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Header } from '../components/Header';
import { Dog, Briefcase, MapPin, ArrowRight, PawPrint } from '@phosphor-icons/react';
import { motion } from 'framer-motion';
import { CITIES } from '../lib/constants';

export default function OnboardingPage() {
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [role, setRole] = useState(null);
  const [city, setCity] = useState('');
  const [loading, setLoading] = useState(false);

  const handleRoleSelect = (selectedRole) => {
    setRole(selectedRole);
    setStep(2);
  };

  const handleComplete = async () => {
    if (!city) return;
    
    setLoading(true);
    try {
      await updateUser({ role, city });
      
      if (role === 'prestador') {
        navigate('/provider/profile');
      } else {
        navigate('/client/home');
      }
    } catch (error) {
      toast.error('Erro ao guardar. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <div className="container mx-auto px-4 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-lg mx-auto"
        >
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-full bg-primary flex items-center justify-center mx-auto mb-4">
              <PawPrint weight="fill" className="w-8 h-8 text-primary-foreground" />
            </div>
            <h1 className="text-3xl font-bold font-outfit">
              Olá, {user?.name?.split(' ')[0]}!
            </h1>
            <p className="text-muted-foreground mt-2">
              Vamos configurar a sua conta
            </p>
          </div>

          {/* Progress indicator */}
          <div className="flex items-center justify-center gap-2 mb-8">
            <div className={`w-3 h-3 rounded-full ${step >= 1 ? 'bg-primary' : 'bg-muted'}`} />
            <div className={`w-12 h-1 rounded ${step >= 2 ? 'bg-primary' : 'bg-muted'}`} />
            <div className={`w-3 h-3 rounded-full ${step >= 2 ? 'bg-primary' : 'bg-muted'}`} />
          </div>

          {step === 1 && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
            >
              <Card>
                <CardHeader>
                  <CardTitle>Como pretende usar o PasseioPerfeito?</CardTitle>
                  <CardDescription>
                    Pode mudar isto mais tarde nas definições
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <Button
                    variant="outline"
                    className={`w-full h-auto p-4 sm:p-6 justify-start ${role === 'cliente' ? 'border-primary bg-primary/5' : ''}`}
                    onClick={() => handleRoleSelect('cliente')}
                    data-testid="role-cliente"
                  >
                    <div className="flex items-start gap-3 sm:gap-4 overflow-hidden">
                      <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <Dog weight="duotone" className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
                      </div>
                      <div className="text-left min-w-0">
                        <h3 className="font-semibold text-sm sm:text-base">Sou dono de animal</h3>
                        <p className="text-xs sm:text-sm text-muted-foreground mt-1 whitespace-normal break-words">
                          Quero encontrar pet sitters e passeadores
                        </p>
                      </div>
                    </div>
                  </Button>

                  <Button
                    variant="outline"
                    className={`w-full h-auto p-4 sm:p-6 justify-start ${role === 'prestador' ? 'border-primary bg-primary/5' : ''}`}
                    onClick={() => handleRoleSelect('prestador')}
                    data-testid="role-prestador"
                  >
                    <div className="flex items-start gap-3 sm:gap-4 overflow-hidden">
                      <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-secondary/10 flex items-center justify-center flex-shrink-0">
                        <Briefcase weight="duotone" className="w-5 h-5 sm:w-6 sm:h-6 text-secondary" />
                      </div>
                      <div className="text-left min-w-0">
                        <h3 className="font-semibold text-sm sm:text-base">Quero ser prestador</h3>
                        <p className="text-xs sm:text-sm text-muted-foreground mt-1 whitespace-normal break-words">
                          Quero oferecer serviços de pet sitting ou passeios
                        </p>
                      </div>
                    </div>
                  </Button>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {step === 2 && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
            >
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <MapPin weight="duotone" className="w-6 h-6 text-primary" />
                    Qual é a sua cidade?
                  </CardTitle>
                  <CardDescription>
                    Iremos mostrar prestadores e serviços na sua zona
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <Select value={city} onValueChange={setCity}>
                    <SelectTrigger className="h-12" data-testid="city-select">
                      <SelectValue placeholder="Selecione a sua cidade" />
                    </SelectTrigger>
                    <SelectContent>
                      {CITIES.map((c) => (
                        <SelectItem key={c.id} value={c.id}>
                          {c.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <div className="flex gap-3">
                    <Button
                      variant="outline"
                      onClick={() => setStep(1)}
                      className="flex-1"
                    >
                      Voltar
                    </Button>
                    <Button
                      onClick={handleComplete}
                      disabled={!city || loading}
                      className="flex-1 rounded-full"
                      data-testid="complete-onboarding"
                    >
                      {loading ? 'A guardar...' : 'Continuar'}
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}
        </motion.div>
      </div>
    </div>
  );
}
